import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const socialLinks = [
    { icon: <FacebookIcon />, href: '#', label: 'Facebook' },
    { icon: <LinkedInIcon />, href: '#', label: 'LinkedIn' },
  ];

  const quickLinks = [
    { label: t('header.home'), path: '/' },
    { label: t('header.services'), path: '/find-services' },
    { label: t('header.findTalent'), path: '/find-talent' },
    { label: t('header.findWork'), path: '/find-work' },
  ];

  const companyLinks = [
    { label: t('footer.aboutUs'), path: '/about' },
    { label: t('footer.contact'), path: '/contact' },
    { label: t('footer.privacy'), path: '/privacy' },
    { label: t('footer.terms'), path: '/terms' },
  ];

  const supportLinks = [
    { label: t('footer.faq'), path: '/faq' },
    { label: t('footer.status'), path: '/status' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0a0a1a',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={5}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                {t('header.logo')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.6 }}>
                {t('footer.description')}
              </Typography>
            </Box>

            {/* Contact Info */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#28A0F0', mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  info@workio.bg
                </Typography>
              </Box>
            </Box>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#a0a0a0',
                    '&:hover': {
                      color: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    },
                  }}
                  aria-label={social.label}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2.33}>
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, fontWeight: 600 }}>
              {t('footer.quickLinks')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  component="button"
                  onClick={() => handleNavigation(link.path)}
                  sx={{
                    color: '#a0a0a0',
                    textDecoration: 'none',
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: '#28A0F0',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6} md={2.33}>
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, fontWeight: 600 }}>
              {t('footer.company')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {companyLinks.map((link, index) => (
                <Link
                  key={index}
                  component="button"
                  onClick={() => handleNavigation(link.path)}
                  sx={{
                    color: '#a0a0a0',
                    textDecoration: 'none',
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: '#28A0F0',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Support */}
          <Grid item xs={12} sm={6} md={2.34}>
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, fontWeight: 600 }}>
              {t('footer.support')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {supportLinks.map((link, index) => (
                <Link
                  key={index}
                  component="button"
                  onClick={() => handleNavigation(link.path)}
                  sx={{
                    color: '#a0a0a0',
                    textDecoration: 'none',
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: '#28A0F0',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
            © {new Date().getFullYear()} {t('header.logo')}. {t('footer.allRightsReserved')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Link
              component="button"
              onClick={() => handleNavigation('/privacy')}
              sx={{
                color: '#a0a0a0',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#28A0F0' },
              }}
            >
              {t('footer.privacy')}
            </Link>
            <Link
              component="button"
              onClick={() => handleNavigation('/terms')}
              sx={{
                color: '#a0a0a0',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#28A0F0' },
              }}
            >
              {t('footer.terms')}
            </Link>
            <Link
              component="button"
              onClick={() => handleNavigation('/cookies')}
              sx={{
                color: '#a0a0a0',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#28A0F0' },
              }}
            >
              {t('footer.cookies')}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
